'use client';

import { useI18n } from '@/contexts/I18nContext';

import { MessageSquarePlus, PanelLeftClose, PanelLeftOpen, Trash2 } from 'lucide-react';

export interface CannyChatSummary {
  id: string;
  title: string;
  updatedAt: string;
}

interface CannySidebarProps {
  chats: CannyChatSummary[];
  activeChatId: string | null;
  collapsed: boolean;
  onToggle: () => void;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
}

/**
 * Сайдбар со списком чатов. Отступы и позиционирование — на логических
 * свойствах (ms/me/start/end), иначе арабский на cannect.ai встанет зеркально
 * относительно всего остального.
 */
export function CannySidebar({
  chats,
  activeChatId,
  collapsed,
  onToggle,
  onSelect,
  onCreate,
  onDelete,
}: CannySidebarProps) {
  const { t } = useI18n();
  // Своя подложка и граница по inline-end: сайдбар должен читаться как
  // самостоятельное боковое меню, а не сливаться с полотном переписки.
  const shell = 'border-e border-white/10 bg-black/30 backdrop-blur-sm';

  if (collapsed) {
    return (
      <div className={`flex flex-col items-center gap-2 p-3 ${shell}`}>
        <button
          type="button"
          onClick={onToggle}
          aria-label={t('canny.sidebar.show')}
          className="liquid-glass flex h-10 w-10 items-center justify-center rounded-full text-white/70 transition-colors hover:text-white"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onCreate}
          aria-label={t('canny.sidebar.new')}
          className="liquid-glass flex h-10 w-10 items-center justify-center rounded-full text-white/70 transition-colors hover:text-white"
        >
          <MessageSquarePlus className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <aside className={`flex w-72 shrink-0 flex-col gap-3 p-3 ${shell}`}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCreate}
          className="liquid-glass flex h-10 flex-1 items-center gap-2 rounded-2xl px-3 text-sm text-white/85 transition-colors hover:text-white"
        >
          <MessageSquarePlus className="h-4 w-4" />
          {t('canny.sidebar.new')}
        </button>
        <button
          type="button"
          onClick={onToggle}
          aria-label={t('canny.sidebar.hide')}
          className="liquid-glass flex h-10 w-10 items-center justify-center rounded-full text-white/60 transition-colors hover:text-white"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto">
        {chats.length === 0 ? (
          <p className="px-3 py-6 text-xs leading-relaxed text-white/40">
            {t('canny.sidebar.empty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {chats.map((chat) => {
              const active = chat.id === activeChatId;
              return (
                <li key={chat.id} className="group relative">
                  <button
                    type="button"
                    onClick={() => onSelect(chat.id)}
                    className={`w-full truncate rounded-xl py-2 pe-9 ps-3 text-start text-sm transition-colors ${
                      active ? 'bg-white/10 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white/90'
                    }`}
                  >
                    {chat.title}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(chat.id)}
                    aria-label={`${t('canny.sidebar.delete')}: ${chat.title}`}
                    className="absolute end-1 top-1/2 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-white/40 transition-colors hover:text-coral-100 group-hover:flex"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </nav>
    </aside>
  );
}
